/**
 * UnifiedAuthService - Single source of truth for authentication
 *
 * Combines functionality from TokenManager, AuthStatusService, TokenService,
 * and parts of AuthService into one cohesive service.
 *
 * Key features:
 * - Single cache for auth status and tokens
 * - Prevents concurrent requests
 * - Backend handles all token refresh logic via /api/auth/status
 * - Simplified API with clear responsibilities
 */

import { COOKIE_NAMES, CookieService } from '@/services/cookies';
import { decodeJWT, type JWTPayload } from '@/utils/jwt';

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || 'https://jobber-api-staging.onrender.com';

export interface AuthStatusResponse {
  all_valid: boolean;
  needs_reauth: string[];
  api_session: {
    valid: boolean;
    expires_at?: number;
    access_token?: string;
    refresh_token?: string;
  };
  jobber: {
    valid: boolean;
    needs_reauth: boolean;
    has_oauth: boolean;
    expires_at?: string;
    account_id?: string;
    access_token?: string;
  };
  eagleview: {
    valid: boolean;
    needs_reauth: boolean;
    has_oauth: boolean;
    has_api_key: boolean;
    expires_at?: string;
    access_token?: string;
    billing_setup: boolean;
  };
  organization: string;
  timestamp: string;
}

export interface RefreshTokenResponse {
  access_token: string;
  expires_in: number;
  expires_at: number;
}

export interface AuthResult {
  success: boolean;
  error?: string;
  org?: string;
  orgName?: string;
  jobberAccountId?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at: number;
}

export interface AuthResult {
  success: boolean;
  error?: string;
  org?: string;
  orgName?: string;
  jobberAccountId?: string;
}

export interface JobberAccount {
  id: string;
  name: string;
}

export class UnifiedAuthService {
  // Cache for auth status
  private static authStatus: AuthStatusResponse | null = null;
  private static statusPromise: Promise<AuthStatusResponse | null> | null = null;
  private static lastStatusCheck: number = 0;

  // Debug mode
  private static debug = process.env.NEXT_PUBLIC_DEBUG === 'true';

  // Cache durations (relaxed as requested)
  private static STATUS_CACHE_DURATION = 120000; // 2 minutes

  /**
   * Check if we're running on the client side
   */
  private static isClient(): boolean {
    return typeof window !== 'undefined';
  }

  /**
   * Get comprehensive auth status from backend
   * Backend handles all token refresh logic internally
   *
   * @param force - Force a fresh check, bypassing cache
   */
  static async getAuthStatus(force: boolean = false): Promise<AuthStatusResponse | null> {
    // Check cache unless forced
    if (!force && UnifiedAuthService.authStatus && UnifiedAuthService.isCacheValid()) {
      if (UnifiedAuthService.debug) {
        console.log('[UnifiedAuth] Returning cached auth status');
      }
      return UnifiedAuthService.authStatus;
    }

    // Prevent concurrent requests
    if (UnifiedAuthService.statusPromise) {
      if (UnifiedAuthService.debug) {
        console.log('[UnifiedAuth] Waiting for existing status check');
      }
      return await UnifiedAuthService.statusPromise;
    }

    // Start new status check
    UnifiedAuthService.statusPromise = UnifiedAuthService.fetchAuthStatus();

    try {
      const status = await UnifiedAuthService.statusPromise;
      return status;
    } finally {
      UnifiedAuthService.statusPromise = null;
    }
  }

  /**
   * Get access token for API requests
   * Uses cached auth status when available
   */
  static async getToken(): Promise<string | null> {
    try {
      // Get auth status (uses cache if valid)
      const status = await UnifiedAuthService.getAuthStatus();

      if (!status || !status.api_session?.valid) {
        if (UnifiedAuthService.debug) {
          console.log('[UnifiedAuth] No valid session');
        }
        return null;
      }

      const token = status.api_session.access_token;

      if (!token) {
        if (UnifiedAuthService.debug) {
          console.log('[UnifiedAuth] No access token in status');
        }
        return null;
      }

      if (UnifiedAuthService.debug) {
        console.log('[UnifiedAuth] Returning access token from status');
      }

      return token;
    } catch (error) {
      console.error('[UnifiedAuth] Error getting token:', error);
      return null;
    }
  }

  /**
   * Ensure we have valid authentication
   * Forces a fresh status check
   */
  static async ensureValidAuth(): Promise<boolean> {
    const status = await UnifiedAuthService.getAuthStatus(true);
    return status?.api_session?.valid || false;
  }

  /**
   * Explicitly refresh tokens
   * Uses the dedicated refresh endpoint
   */
  static async refreshAuth(): Promise<boolean> {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('[UnifiedAuth] Refresh token expired');
          UnifiedAuthService.clearAuth();
          return false;
        }

        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[UnifiedAuth] Refresh failed:', error);
        return false;
      }

      const data: RefreshTokenResponse = await response.json();
      console.log('[UnifiedAuth] Token refreshed successfully');

      // Clear cache to force fresh status on next request
      UnifiedAuthService.clearCache();

      // Update cookie metadata if we have org info
      const org = UnifiedAuthService.getOrg();
      if (org) {
        UnifiedAuthService.setTokenMetadata(data.expires_at, org);
      }

      return true;
    } catch (error) {
      console.error('[UnifiedAuth] Refresh network error:', error);
      return false;
    }
  }

  /**
   * Clear all authentication state
   */
  static clearAuth(): void {
    UnifiedAuthService.clearCache();
    UnifiedAuthService.clearCookies();
  }

  /**
   * Clear the status cache
   */
  static clearCache(): void {
    if (UnifiedAuthService.debug) {
      console.log('[UnifiedAuth] Clearing cache');
    }
    UnifiedAuthService.authStatus = null;
    UnifiedAuthService.lastStatusCheck = 0;
  }

  // ============================================
  // Cookie Management (from TokenService)
  // ============================================

  /**
   * Set token metadata in cookies (not the actual tokens)
   */
  static setTokenMetadata(
    expiresAt: number,
    org: string,
    orgName?: string,
    jobberAccountId?: string
  ): void {
    if (!UnifiedAuthService.isClient()) return;

    CookieService.setInBrowser(COOKIE_NAMES.EXPIRES_AT, expiresAt.toString(), {
      maxAge: 2592000,
      path: '/',
    });

    CookieService.setInBrowser(COOKIE_NAMES.ORG, org, {
      maxAge: 2592000,
      path: '/',
    });

    if (orgName) {
      CookieService.setInBrowser(COOKIE_NAMES.ORG_NAME, orgName, {
        maxAge: 2592000,
        path: '/',
      });
    }

    if (jobberAccountId) {
      CookieService.setInBrowser(COOKIE_NAMES.JOBBER_ACCOUNT_ID, jobberAccountId, {
        maxAge: 2592000,
        path: '/',
      });
    }
  }

  /**
   * Set tokens from a response (used after OAuth callback)
   */
  static setTokens(
    response: TokenResponse,
    org: string,
    orgName?: string,
    jobberAccountId?: string
  ): void {
    UnifiedAuthService.setTokenMetadata(response.expires_at, org, orgName, jobberAccountId);
    // Clear cache to force fresh status on next request
    UnifiedAuthService.clearCache();
  }

  static getOrg(): string | null {
    if (!UnifiedAuthService.isClient()) return null;
    return CookieService.getFromBrowser(COOKIE_NAMES.ORG) || null;
  }

  static getOrgName(): string | null {
    if (!UnifiedAuthService.isClient()) return null;
    return CookieService.getFromBrowser(COOKIE_NAMES.ORG_NAME) || null;
  }

  static getJobberAccountId(): string | null {
    if (!UnifiedAuthService.isClient()) return null;
    return CookieService.getFromBrowser(COOKIE_NAMES.JOBBER_ACCOUNT_ID) || null;
  }

  static getExpiresAt(): number | null {
    if (!UnifiedAuthService.isClient()) return null;
    const expiresAtStr = CookieService.getFromBrowser(COOKIE_NAMES.EXPIRES_AT);
    if (!expiresAtStr) return null;
    return parseInt(expiresAtStr, 10);
  }

  private static clearCookies(): void {
    if (!UnifiedAuthService.isClient()) return;

    CookieService.removeFromBrowser(COOKIE_NAMES.EXPIRES_AT);
    CookieService.removeFromBrowser(COOKIE_NAMES.ORG);
    CookieService.removeFromBrowser(COOKIE_NAMES.ORG_NAME);
    CookieService.removeFromBrowser(COOKIE_NAMES.JOBBER_ACCOUNT_ID);
  }

  // ============================================
  // Auth Status Helpers
  // ============================================

  /**
   * Get the appropriate redirect URL based on what needs reauth
   */
  static getRedirectUrl(needsReauth: string[], returnTo: string): string {
    if (needsReauth.length === 0) {
      return returnTo;
    }

    // Priority: API session and Jobber auth are handled by the same flow
    if (needsReauth.includes('api_session') || needsReauth.includes('jobber')) {
      return `/jobber/auth?return_to=${encodeURIComponent(returnTo)}`;
    }

    // EagleView is secondary
    if (needsReauth.includes('eagleview')) {
      return `/eagleview/auth?return_to=${encodeURIComponent(returnTo)}`;
    }

    return returnTo;
  }

  /**
   * Check if all services are valid
   */
  static isAllServicesValid(status: AuthStatusResponse): boolean {
    return status.all_valid;
  }

  /**
   * Check if user has valid API session
   */
  static hasValidSession(status: AuthStatusResponse): boolean {
    return status.api_session?.valid || false;
  }

  /**
   * Check if user has valid Jobber OAuth
   */
  static hasValidJobberAuth(status: AuthStatusResponse): boolean {
    return status.jobber?.valid || false;
  }

  /**
   * Check if user has valid EagleView auth (OAuth or API key)
   */
  static hasValidEagleViewAuth(status: AuthStatusResponse): boolean {
    return status.eagleview?.valid || false;
  }

  // ============================================
  // Authenticated Requests
  // ============================================

  /**
   * Make an authenticated request with automatic token handling
   */
  static async makeAuthenticatedRequest<T = any>(
    url: string,
    options: RequestInit = {}
  ): Promise<{ data?: T; error?: string }> {
    try {
      // Get token
      const token = await UnifiedAuthService.getToken();

      if (!token) {
        console.log('[UnifiedAuth] No valid token for request');
        return { error: 'No valid authentication' };
      }

      // Build headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      // Add auth headers for external API calls
      const isExternal = url.startsWith('http') || url.startsWith('//');
      if (isExternal) {
        headers['Authorization'] = `Bearer ${token}`;
        const org = UnifiedAuthService.getOrg();
        if (org) {
          headers['X-Organization'] = org;
        }
      }

      // Make request
      let response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      // Handle 401 by refreshing and retrying once
      if (response.status === 401) {
        console.log('[UnifiedAuth] Got 401, attempting refresh and retry');

        const refreshed = await UnifiedAuthService.refreshAuth();
        if (refreshed) {
          // Get new token
          const newToken = await UnifiedAuthService.getToken();
          if (newToken && isExternal) {
            headers['Authorization'] = `Bearer ${newToken}`;
          }

          // Retry request
          response = await fetch(url, {
            ...options,
            headers,
            credentials: 'include',
          });
        } else {
          return { error: 'Authentication failed - please login again' };
        }
      }

      if (!response.ok) {
        const error = await response.text();
        return { error: `Request failed: ${error}` };
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('[UnifiedAuth] Request error:', error);
      return {
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // ============================================
  // OAuth and Authentication Flows
  // ============================================

  /**
   * Get Jobber OAuth authorization URL
   */
  static getJobberAuthUrl(org: string, returnTo: string): string {
    const params = new URLSearchParams({
      org,
      return_to: returnTo,
    });
    return `${API_BASE_URL}/api/auth/jobber/start?${params.toString()}`;
  }

  /**
   * Helper to make absolute URLs for OAuth redirects
   */
  private static makeAbsoluteUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) return url;
    const webBase = process.env.NEXT_PUBLIC_WEB_BASE_URL;
    if (webBase) return new URL(url, webBase).toString();
    if (typeof window !== 'undefined' && window.location?.origin) {
      return new URL(url, window.location.origin).toString();
    }
    return url;
  }

  /**
   * Start EagleView OAuth immediately using short-lived JWT
   * Returns URL for direct browser navigation (no fetch!)
   */
  static startEagleViewOAuthImmediate(jwt: string, returnTo: string, org?: string): string {
    const absoluteReturnTo = UnifiedAuthService.makeAbsoluteUrl(returnTo);

    // Build query params - pass JWT as query param for browser navigation
    const params = new URLSearchParams({
      token: jwt, // JWT as query param since we can't set headers on navigation
      return_to: absoluteReturnTo,
    });

    if (org) {
      params.append('org', org);
    }

    // Return URL for direct browser navigation to Python server
    // Python server will validate JWT and redirect to EagleView
    return `${API_BASE_URL}/api/auth/eagleview/authorize?${params.toString()}`;
  }

  /**
   * Start EagleView OAuth immediately using access token from auth state
   * Returns URL for direct browser navigation (no fetch!)
   * Gets access token and org from current auth state
   */
  static async startEagleViewOAuthImmediateWithAuth(returnTo: string): Promise<string | null> {
    try {
      // Get access token from auth state
      const accessToken = await UnifiedAuthService.getToken();
      if (!accessToken) {
        console.error('[UnifiedAuth] No access token available for EagleView OAuth');
        return null;
      }

      // Get org from auth state
      const org = UnifiedAuthService.getOrg();
      if (!org) {
        console.error('[UnifiedAuth] No organization available for EagleView OAuth');
        return null;
      }

      // Use the existing immediate OAuth function with auth state values
      return UnifiedAuthService.startEagleViewOAuthImmediate(accessToken, returnTo, org);
    } catch (error) {
      console.error('[UnifiedAuth] Error starting EagleView OAuth with auth state:', error);
      return null;
    }
  }

  /**
   * Start EagleView OAuth via Next.js proxy (uses httpOnly cookie)
   */
  static async startEagleViewOAuthViaProxy(returnTo: string): Promise<string> {
    const absoluteReturnTo = UnifiedAuthService.makeAbsoluteUrl(returnTo);

    try {
      const response = await fetch(
        `/api/auth/eagleview/start?return_to=${encodeURIComponent(absoluteReturnTo)}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get EagleView authorization URL');
      }

      const data = await response.json();
      return data.redirect;
    } catch (error) {
      console.error('Failed to start EagleView OAuth via proxy:', error);
      throw error;
    }
  }

  /**
   * Get EagleView auth URL (deprecated - kept for backward compatibility)
   */
  static getEagleViewAuthUrl(returnTo: string): string {
    console.warn(
      'getEagleViewAuthUrl is deprecated. Use startEagleViewOAuthImmediate or startEagleViewOAuthViaProxy instead.'
    );
    const params = new URLSearchParams({
      return_to: returnTo,
    });
    return `${API_BASE_URL}/api/auth/eagleview/authorize?${params.toString()}`;
  }

  /**
   * Exchange short-lived JWT for session tokens after OAuth callback
   */
  static async exchangeForSessionToken(shortLivedToken: string, org: string): Promise<AuthResult> {
    try {
      // First, decode the JWT to extract org name and jobber account info
      const jwtPayload = decodeJWT(shortLivedToken) as JWTPayload;
      console.log('[UnifiedAuth] JWT Payload:', jwtPayload);
      const orgName = jwtPayload?.jobber_account?.name;
      const jobberAccountId = jwtPayload?.jobber_account?.id;
      console.log(
        '[UnifiedAuth] Extracted from JWT - orgName:',
        orgName,
        'jobberAccountId:',
        jobberAccountId
      );

      const response = await fetch('/api/auth/exchange', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${shortLivedToken}`,
          'Content-Type': 'application/json',
          'X-Org-Id': org,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.text();
        return {
          success: false,
          error: `Failed to exchange token: ${error}`,
        };
      }

      const data = await response.json();

      if (data.expires_at && data.org) {
        UnifiedAuthService.setTokens(
          {
            access_token: '',
            refresh_token: '',
            expires_in: 0,
            expires_at: data.expires_at,
          },
          data.org,
          orgName,
          jobberAccountId
        );
      }

      return {
        success: true,
        org: data.org,
        orgName,
        jobberAccountId,
      };
    } catch (error) {
      console.error('Token exchange error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Refresh access token (compatibility wrapper)
   */
  static async refreshAccessToken(): Promise<AuthResult> {
    const org = UnifiedAuthService.getOrg();

    if (!org) {
      console.log('[UnifiedAuth] No org context for refresh');
      return {
        success: false,
        error: 'No organization context',
      };
    }

    try {
      console.log('[UnifiedAuth] Attempting token refresh');

      const refreshed = await UnifiedAuthService.refreshAuth();

      if (!refreshed) {
        console.error('[UnifiedAuth] Token refresh failed');
        return {
          success: false,
          error: 'Failed to refresh token',
        };
      }

      console.log('[UnifiedAuth] Token refreshed successfully');
      return {
        success: true,
        org,
      };
    } catch (error) {
      console.error('[UnifiedAuth] Token refresh network error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Fetch Jobber account information
   */
  static async fetchJobberAccount(org: string): Promise<JobberAccount | null> {
    try {
      const result = await UnifiedAuthService.makeApiRequest<JobberAccount>(
        `api/auth/jobber/account?org=${encodeURIComponent(org)}`
      );

      if (result.error) {
        console.error('Failed to fetch Jobber account:', result.error);
        return null;
      }

      if (result.data) {
        // Store the fetched data in cookies for future use
        UnifiedAuthService.setTokens(
          {
            access_token: '',
            refresh_token: '',
            expires_in: 0,
            expires_at: UnifiedAuthService.getExpiresAt() || Math.floor(Date.now() / 1000) + 43200,
          },
          org,
          result.data.name,
          result.data.id
        );
      }

      return result.data || null;
    } catch (error) {
      console.error('Error fetching Jobber account:', error);
      return null;
    }
  }

  /**
   * Logout and clear all auth state
   */
  static async logout(): Promise<void> {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      UnifiedAuthService.clearAuth();
    }
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    const expiresAt = UnifiedAuthService.getExpiresAt();
    if (!expiresAt) return false;

    const now = Math.floor(Date.now() / 1000);
    return now < expiresAt;
  }

  // ============================================
  // API Request Helpers
  // ============================================

  /**
   * Make a request to the external backend API (Python/Go backend)
   * @param endpoint - API endpoint path without leading slash (e.g., "auth/jobber/account")
   * @param options - Request options
   */
  static async makeApiRequest<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ data?: T; error?: string }> {
    // Ensure endpoint doesn't start with slash for consistency
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = `${API_BASE_URL}/${cleanEndpoint}`;

    // Use the existing makeAuthenticatedRequest with the full URL
    return UnifiedAuthService.makeAuthenticatedRequest<T>(url, options);
  }

  /**
   * Make a request to internal Next.js API routes
   * @param endpoint - Route path (e.g., "/api/auth/exchange")
   * @param options - Request options
   */
  static async makeInternalRequest<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ data?: T; error?: string }> {
    // Ensure endpoint starts with /api/ for internal routes
    if (!endpoint.startsWith('/api/')) {
      console.warn(
        '[UnifiedAuth] Internal request endpoint should start with /api/, got:',
        endpoint
      );
    }

    // For internal requests, we don't need auth headers since cookies are included
    return UnifiedAuthService.makeAuthenticatedRequest<T>(endpoint, options);
  }

  // ============================================
  // Private Methods
  // ============================================

  /**
   * Check if the status cache is still valid
   */
  private static isCacheValid(): boolean {
    const now = Date.now();
    return now - UnifiedAuthService.lastStatusCheck < UnifiedAuthService.STATUS_CACHE_DURATION;
  }

  /**
   * Fetch auth status from the backend
   */
  private static async fetchAuthStatus(): Promise<AuthStatusResponse | null> {
    try {
      if (UnifiedAuthService.debug) {
        console.log('[UnifiedAuth] Fetching auth status from backend');
      }

      const response = await fetch('/api/auth/status', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('[UnifiedAuth] Authentication required');
          UnifiedAuthService.clearAuth();
          return null;
        }

        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[UnifiedAuth] Status check failed:', error);
        return null;
      }

      const data: AuthStatusResponse = await response.json();

      if (UnifiedAuthService.debug) {
        console.log('[UnifiedAuth] Auth status retrieved:', {
          all_valid: data.all_valid,
          needs_reauth: data.needs_reauth,
        });
      }

      // Update cache
      UnifiedAuthService.authStatus = data;
      UnifiedAuthService.lastStatusCheck = Date.now();

      // Update cookie metadata if we got org info
      if (data.organization) {
        const expiresAt = data.api_session?.expires_at;
        if (expiresAt) {
          UnifiedAuthService.setTokenMetadata(expiresAt, data.organization);
        }
      }

      // Update Jobber account info if available
      if (data.jobber?.account_id) {
        const org = UnifiedAuthService.getOrg();
        const orgName = UnifiedAuthService.getOrgName();
        const expiresAt = UnifiedAuthService.getExpiresAt();
        if (org && expiresAt) {
          UnifiedAuthService.setTokenMetadata(
            expiresAt,
            org,
            orgName || undefined,
            data.jobber.account_id
          );
        }
      }

      return data;
    } catch (error) {
      console.error('[UnifiedAuth] Network error fetching status:', error);
      return null;
    }
  }

  /**
   * Get debug information about the current auth state
   */
  static getDebugInfo(): {
    hasStatus: boolean;
    cacheAge: string | null;
    org: string | null;
    expiresAt: string | null;
    isValid: boolean;
    allServicesValid: boolean;
    needsReauth: string[];
  } {
    const now = Date.now();
    const cacheAge = UnifiedAuthService.lastStatusCheck
      ? `${Math.round((now - UnifiedAuthService.lastStatusCheck) / 1000)} seconds`
      : null;

    const expiresAt = UnifiedAuthService.getExpiresAt();

    return {
      hasStatus: !!UnifiedAuthService.authStatus,
      cacheAge,
      org: UnifiedAuthService.getOrg(),
      expiresAt: expiresAt ? new Date(expiresAt * 1000).toLocaleString() : null,
      isValid: UnifiedAuthService.authStatus?.api_session?.valid || false,
      allServicesValid: UnifiedAuthService.authStatus?.all_valid || false,
      needsReauth: UnifiedAuthService.authStatus?.needs_reauth || [],
    };
  }
}

// Export alias for backward compatibility
export const AuthService = UnifiedAuthService;
